/**
 * SOUNDS.JS - Configuration centralisée des sons
 * 
 * Ce fichier configure tous les sons de l'application, avec leurs paramètres
 * de lecture, variations, et touches clavier associées.
 * 
 * Les sons sont chargés et gérés par Howler.js dans le AudioProvider.
 * Ils sont SÉPARÉS des ressources visuelles pour une meilleure organisation
 * et des optimisations spécifiques à l'audio.
 * 
 * PARAMÈTRES DE CONFIGURATION:
 * - files: Liste des fichiers audio (pour variations)
 * - minDelta: Délai minimum entre deux lectures (ms) - anti-spam
 * - velocityMin: Vélocité minimum pour déclencher le son
 * - velocityMultiplier: Multiplicateur de vélocité pour le volume
 * - volumeMin/Max: Plage de volume
 * - rateMin/Max: Plage de pitch (vitesse de lecture)
 * - keyboard: Touche clavier pour déclencher (optionnel)
 * - loop: Lecture en boucle continue (optionnel, défaut: false)
 * 
 * OPTIMISATIONS IMPLÉMENTÉES:
 * - Préchargement avec Howler.js
 * - Limitation de fréquence (minDelta)
 * - Variations aléatoires (pitch, volume)
 * - Support de multiples variantes par son
 * - Support de la lecture en boucle pour musiques d'ambiance
 */

export const SOUNDS_CONFIG = {
  // ============================================
  // SON DE RÉVÉLATION
  // ============================================
  // Joué lors de l'apparition d'objets dans la scène
  reveal: {
    files: ['/sounds/reveal/wow.mp3'],
    minDelta: 100,                  // 100ms minimum entre lectures
    velocityMin: 0,                 // Pas de vélocité minimum
    velocityMultiplier: 1,          // Pas de modification de volume
    volumeMin: 1,                   // Volume constant
    volumeMax: 1,
    rateMin: 1,                     // Pitch normal
    rateMax: 1,                     // Pas de variation
    keyboard: null                  // Pas de touche clavier
  },

  // ============================================
  // KLAXON (HORN)
  // ============================================
  // Joué via la touche 'H' du clavier
  // Plusieurs variantes pour éviter la répétition
  horn: {
    files: [
      '/sounds/horns/horn-1.mp3',
      '/sounds/horns/horn-2.mp3'
    ],
    minDelta: 500,                  // 500ms entre klaxons (anti-spam)
    velocityMin: 0,                 // Pas de vélocité minimum
    velocityMultiplier: 1,          // Volume constant
    volumeMin: 0.8,                 // Volume élevé mais variable
    volumeMax: 1,
    rateMin: 0.95,                  // Légère variation de pitch
    rateMax: 1.05,
    keyboard: 'h'                   // Touche H pour déclencher
  },

  // ============================================
  // Moods & Ambiances
  // ============================================
  // Joué via clic sur la radio
  // En boucle pour créer une ambiance continue
  mood1: {
    files: ['/sounds/moods/midnight.mp3'],
    minDelta: 3000,                  // 3s entre déclenchements (anti-spam)
    velocityMin: 0,                 // Pas de vélocité minimum
    velocityMultiplier: 1,          // Pas de modification de volume
    volumeMin: 0.8,                 // Volume ambiance (compensé par courbe quadratique)
    volumeMax: 0.8,                 // Volume constant
    rateMin: 1,                     // Pitch normal
    rateMax: 1,                     // Pas de variation
    keyboard: null,                 // Pas de touche clavier (clic sur radio)
    loop: true                      // En boucle continue
  },

  // ============================================
  // Intéractions
  // ============================================
  // son rapides et courts 

  hihi: { //activé lors du clic sur sausage
    files: ['/sounds/sausage/hihi.mp3'],
    minDelta: 500,                  // 500ms entre déclenchements (anti-spam)
    velocityMin: 0,                 // Pas de vélocité minimum
    velocityMultiplier: 1,          // Pas de modification de volume
    volumeMin: 0.8,                 // Volume ambiance (compensé par courbe quadratique)
    volumeMax: 1,                 // Volume constant
    rateMin: 1,                     // Pitch normal
    rateMax: 1,                     // Pas de variation
    keyboard: null                 // Pas de touche clavier (clic sur radio)
  }
}

// ============================================
// BPM DES MUSIQUES (pour synchronisation)
// ============================================
export const SONG_BPM = {
  mood1: 120,  // BPM de midnight.mp3
}

export default {
  SOUNDS_CONFIG,
  SONG_BPM
}

// ============================================
// GUIDE D'UTILISATION DU BPM:
// ============================================
// Pour ajouter le BPM d'une nouvelle musique :
// 1. Déterminer le BPM avec un logiciel audio (Audacity, DAW, etc.)
// 2. Ajouter une entrée dans SONG_BPM avec le même nom que dans SOUNDS_CONFIG
// 3. Le système de danse utilisera ce BPM pour synchroniser les animations

/*
 * ============================================
 * SONS SUPPLÉMENTAIRES À AJOUTER
 * ============================================
 * Décommentez et configurez selon vos besoins dans SOUNDS_CONFIG ci-dessus
 * 
 * Exemples :
 * 
 * Impact - Collisions physiques
 * Moteur - Son continu  
 * UI Hover - Survol d'élément
 */

/**
 * GUIDE D'UTILISATION:
 * 
 * 1. Ajouter un nouveau son:
 *    - Copier un bloc de configuration existant
 *    - Modifier les paramètres selon vos besoins
 *    - Placer les fichiers audio dans /static/sounds/
 * 
 * 2. Variations aléatoires:
 *    - Ajouter plusieurs fichiers dans le tableau files[]
 *    - Le système choisira aléatoirement à chaque lecture
 * 
 * 3. Sons déclenchés au clavier:
 *    - Définir la propriété keyboard: 'touche'
 *    - Ex: keyboard: 'h' pour la touche H
 *    - null si pas de touche
 * 
 * 4. Sons basés sur vélocité (collisions):
 *    - Augmenter velocityMultiplier (ex: 2)
 *    - Définir velocityMin (seuil minimum)
 *    - Le volume augmentera avec la force de l'impact
 * 
 * 5. Anti-spam (minDelta):
 *    - Sons UI: 100-200ms
 *    - Collisions: 50ms
 *    - Klaxons/effets: 500ms+
 *    - Sons continus: 0ms
 */

